import React from 'react';
import { cn } from '@/lib/utils';

/*
 * Round 15 (2026-09-30): the small pieces the two round-15 Slot Stats cards share (CloseFriendsCard,
 * SavedCollectionCard): a state chip, a figure, the compact one-line card (a part that is off for the slot, or
 * a figure that could not be loaded) and the "hidden" log line.
 */

export const NOT_RECORDED = 'not recorded';

const TONES = {
  ok: 'border-[hsl(var(--success)/.45)] bg-[hsl(var(--success)/.12)] text-[hsl(var(--success))]',
  info: 'border-[hsl(var(--info)/.45)] bg-[hsl(var(--info)/.12)] text-[hsl(var(--info))]',
  warn: 'border-[hsl(var(--warning)/.45)] bg-[hsl(var(--warning)/.12)] text-[hsl(var(--warning))]',
  muted: 'border-white/10 bg-white/[.03] text-muted-foreground',
};

/** A state as a pill: a dot plus the name (the colour never carries the meaning alone). */
export function StateChip({ label, tone = 'muted', testid, code }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium leading-5', TONES[tone] || TONES.muted)}
      data-testid={testid} data-state={code}>
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
      {label}
    </span>
  );
}

/**
 * One figure: a label, the value (a missing one reads "not recorded", muted - never 0) and a caption.
 * `value` null / undefined = not recorded.
 */
export function Figure({ label, value, suffix, caption, testid, children }) {
  const missing = value === null || value === undefined;
  return (
    <div className="min-w-0 rounded-xl border border-white/5 bg-white/[.02] px-3.5 py-3" data-testid={testid}>
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={cn('mt-1 leading-tight', missing ? 'text-sm text-muted-foreground' : 'text-xl font-semibold text-foreground tnum')}
        data-testid={testid && `${testid}-value`}>
        {missing ? NOT_RECORDED : value}
        {!missing && suffix ? <span className="ml-1 text-sm font-normal text-muted-foreground">{suffix}</span> : null}
      </p>
      {caption && <p className="mt-1 text-xs text-muted-foreground text-pretty" data-testid={testid && `${testid}-caption`}>{caption}</p>}
      {children}
    </div>
  );
}

/** A one-line card: the part is off for this slot, or its figures could not be read. */
export function CompactLine({ icon: Icon, title, children, testid, tone }) {
  return (
    <section className="aurora-card flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 px-4 py-3 text-sm sm:px-5" data-testid={testid}
      aria-label={title}>
      {Icon && <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />}
      <span className="font-heading font-semibold text-foreground">{title}</span>
      <span className={cn('min-w-0 text-pretty', tone === 'error' ? 'text-destructive' : 'text-muted-foreground')}>{children}</span>
    </section>
  );
}

const logged = new Set();

/** One console line per page load (a card re-renders; the reason does not change). */
function logOnce(level, line) {
  if (logged.has(line)) return;
  logged.add(line);
  // eslint-disable-next-line no-console
  (level === 'warn' ? console.warn : console.info)(line);
}

/** Logs once per page load why a round-15 card is not drawn (an older server, its part off, no fields). */
export function logHidden(card, slotName, why) {
  logOnce('info', `[R15-UI] ${card} card not drawn for ${slotName}: ${why}`);
}

/** Logs once per page load that a round-15 route failed (the card shows a one-line error). */
export function logFailed(card, slotName, c) {
  logOnce('warn', `[R15-UI] ${card} figures of ${slotName} could not be loaded: HTTP ${c.status ?? '-'} ${c.code || ''} ${c.message || ''}`.trim());
}
